<script>
    import LyButtons from '../../../layouts/forms/LyButtons.svelte';
    import LyButton from '../../../layouts/forms/LyButton.svelte';
    import LyHeader from '../../../layouts/components/LyHeader.svelte';
    import LyForm from '../../../layouts/forms/LyForm.svelte';
    import LyFieldset from '../../../layouts/forms/LyFieldset.svelte';
    import LyHeadline from '../../../layouts/components/LyHeadline.svelte';
    import LyInputText from '../../../layouts/forms/LyInputText.svelte';
    import LyInputDate from '../../../layouts/forms/LyInputDate.svelte';
    import LyInputCheckbox from '../../../layouts/forms/LyInputCheckbox.svelte';
    import LyInputSelect from '../../../layouts/forms/LyInputSelect.svelte';
    import { ApiService } from '../../../services/ApiService.js';
    import { UserStore } from '../../../stores/UserStore.svelte.js';
    import LyGrid2 from '../../../layouts/components/LyGrid2.svelte';
    import LyGrid3 from '../../../layouts/components/LyGrid3.svelte';
    import LyP from '../../../layouts/components/LyP.svelte';
    import { riskOptions, roleOptions, humanOversightOptions } from './aiAnnouncementOptions.js';

    /**
     * @typedef {import('../../../types/api.d.js').ApiAiAnnouncementData} ApiAiAnnouncementData
     */

    /**
     * @type {{
     *      system?: string,
     *      company?: string,
     *      used_for?: string,
     *      used_since?: string,
     *      jobs?: string,
     *      data_type?: string,
     *      is_pii?: boolean,
     *      risk?: string,
     *      role?: string,
     *      human_oversight?: string,
     *      is_art4?: boolean,
     *      is_art50?: boolean,
     *      is_art14?: boolean,
     *      is_art27?: boolean,
     *      is_dsfa?: boolean,
     *      is_avv?: boolean,
     *      is_eu_hosting?: boolean,
     *      todo_message?: string,
     *      todo_name?: string,
     *      todo_till?: string,
     *      cancelButton?: () => void,
     * }}
     */
    let {
        system = '',
        company = '',
        used_for = '',
        used_since = '',
        jobs = '',
        data_type = '',
        is_pii = false,
        risk = 'minimal',
        role = 'deployer',
        human_oversight = 'in',
        is_art4 = false,
        is_art50 = false,
        is_art14 = false,
        is_art27 = false,
        is_dsfa = false,
        is_avv = false,
        is_eu_hosting = false,
        todo_message = '',
        todo_name = '',
        todo_till = '',
        cancelButton = () => {},
    } = $props();
    let disableForm = $state(false);
    const api = new ApiService();

    async function trySave() {
        disableForm = true;

        const data = /** @type {ApiAiAnnouncementData} */ ({
            ai_announcement_id: null,
            user_id: UserStore.userId,
            system,
            company,
            used_for,
            used_since,
            jobs,
            data_type,
            is_pii,
            risk,
            role,
            human_oversight,
            is_art4,
            is_art50,
            is_art14,
            is_art27,
            is_dsfa,
            is_avv,
            is_eu_hosting,
            todo_message,
            todo_name,
            todo_till,
        });

        const row = await api.postAiAnnouncement(data);
        disableForm = false;
        if (row.ok) {
            cancelButton();
        }
    }
</script>

<LyHeader>
    <LyHeadline weight={2}>Arbeitsblatt AI-Inventur anlegen</LyHeadline>
</LyHeader>
<LyForm mt="4" tag="div" wrapper={true}>
    <LyFieldset>
        <LyGrid2 tag="div" mb={true}>
            <LyInputText
                name="system"
                required={true}
                label="System, Bezeichnung"
                placeholder=""
                autocomplete={true}
                autocorrect={true}
                description="Eindeutige interne Bezeichnung oder offizieller Produktname, z.B. ChatGPT Enterprise oder DeepL Pro."
                bind:value={system} />
            <LyInputText
                name="company"
                required={true}
                label="Anbieter oder Hersteller"
                placeholder=""
                autocomplete={true}
                autocorrect={true}
                description="Das Unternehmen, das die Software betreibt bzw. entwickelt hat, z.B. Microsoft, OpenAI oder JetBrains. "
                bind:value={company} />
        </LyGrid2>

        <LyInputDate
            name="used_since"
            label="Genutzt seit"
            description="Seit wann nutzt Du dieses System bereits?"
            bind:value={used_since} />
    </LyFieldset>

    <LyFieldset mt={true}>
        <LyGrid2 tag="div">
            <LyInputText
                name="used_for"
                required={true}
                label="Eingesetzt im Bereich"
                description="Die Abteilung oder der Geschäftsprozess, in dem die KI genutzt wird, z.B. Marketing, HR, Buchhaltung oder IT."
                placeholder=""
                autocomplete={true}
                autocorrect={true}
                bind:value={used_for} />
            <LyInputCheckbox
                name="is_pii"
                label="Personenbezug (PII)"
                description="PII = Personally Identifiable Information. Lassen sich die Daten echten Menschen zuordnen (Namen, E-Mails, Fotos, Gehalt, Telefonnummer)?"
                bind:value={is_pii} />
            <LyInputText
                type="area"
                name="jobs"
                label="Welche Funktion erfüllt das System?"
                description="Konkrete Anwendungsfälle: Was genau erzeugt oder analysiert die KI? Z.B. Übersetzen von Mails oder Zusammenfassen von Verträgen."
                placeholder=""
                autocomplete={true}
                autocorrect={true}
                bind:value={jobs} />
            <LyInputText
                type="area"
                name="data_type"
                label="Welche Datenarten werden verarbeitet?"
                description="Welche Inputs tippt ihr ein? z.B. Kundennamen, Quellcode, Rechnungsdaten oder rein öffentliche Marketingtexte."
                placeholder=""
                autocomplete={true}
                autocorrect={true}
                bind:value={data_type} />
        </LyGrid2>
    </LyFieldset>

    <LyFieldset mt={true}>
        <LyGrid2 tag="div">
            <LyInputSelect name="risk" label="Risikoklasse" options={riskOptions} bind:value={risk} />
            <LyP>
                <span class="text-xs">
                    <strong>MINIMAL</strong>: z.B. Spamfilter oder einfache KI-Hilfen (keine gesetzlichen Auflagen).
                    <br /><strong>BEGRENZT (Art. 50)</strong>: z.B. Chatbots &amp; Text-Generatoren (Transparenz- &amp;
                    Kennzeichnungspflicht).
                    <br /><strong>HIGH-RISK (Annex III)</strong>: z.B. CV-Screening, Kreditvergabe, Medizin (strenge
                    Compliance-Pflichten).
                    <br /><strong>VERBOTEN (Art. 5)</strong>: z.B. Social Scoring oder manipulative biometrische
                    Überwachung.
                    <br /><strong>Unklar</strong>: Klassifizierung erfolgt gemeinsam im Workshop oder mit dem DSB.
                </span>
            </LyP>
        </LyGrid2>

        <LyGrid2 tag="div" mt={true}>
            <LyInputSelect name="role" label="Unsere Rolle" options={roleOptions} bind:value={role} />
            <LyP>
                <span class="text-xs">
                    <strong>BETREIBER (Deployer)</strong>: Wir setzen ein fertiges KI-System eines Fremdanbieters bei
                    uns ein.
                    <br /><strong>ANBIETER (Provider)</strong>: Wir entwickeln oder trainieren die KI selbst, um sie am
                    Markt zu vertreiben.
                    <br /><strong>Distributor / Importer</strong>: Wir vertreiben oder importieren fremde KI-Software
                    unter fremdem Namen.
                </span>
            </LyP>
        </LyGrid2>
        <LyGrid2 tag="div" mt={true}>
            <LyInputSelect
                name="human_oversight"
                label="Human Oversight"
                options={humanOversightOptions}
                bind:value={human_oversight} />
            <LyP>
                <span class="text-xs">
                    <strong>Human-in-the-Loop</strong>: Ein Mensch prüft und gibt jeden einzelnen KI-Vorschlag aktiv
                    frei.
                    <br /><strong>Human-on-the-Loop</strong>: KI arbeitet autark, ein Mensch überwacht den Prozess und
                    kann eingreifen.
                    <br /><strong>Human-out-of-the-Loop</strong>: Vollautomatischer Betrieb, menschliche Kontrolle nur
                    über nachträgliche Audits
                </span>
            </LyP>
        </LyGrid2>
    </LyFieldset>

    <LyFieldset mt={true} label="Greifende Pflichten">
        <LyGrid3 tag="div">
            <LyInputCheckbox
                name="is_art4"
                label="Art. 4 (AI Literacy)"
                description="Belegschaft im sicheren Umgang mit KI schulen"
                bind:value={is_art4} />
            <LyInputCheckbox
                name="is_art50"
                label="Art. 50 (Transparenz)"
                description="KI-generierte Texte/Bilder öffentlich kennzeichnen"
                bind:value={is_art50} />
            <LyInputCheckbox
                name="is_art14"
                label="Art. 14/26 (Oversight)"
                description="Menschliche Aufsicht &amp; Stopp-Mechanismen festlegen"
                bind:value={is_art14} />
            <LyInputCheckbox
                name="is_art27"
                label="Art. 27 (FRIA)"
                description="Grundrechte-Folgenabschätzung (nur bei High-Risk)"
                bind:value={is_art27} />
            <LyInputCheckbox
                name="is_dsfa"
                label="DSFA"
                description="Datenschutz-Folgenabschätzung gem. Art. 35 DSGVO"
                bind:value={is_dsfa} />
            <LyInputCheckbox
                name="is_avv"
                label="AVV-Anpassung"
                description="Auftragsverarbeitungsvertrag mit Anbieter schließen"
                bind:value={is_avv} />
            <LyInputCheckbox
                name="is_eu_hosting"
                label="EU-Hosting"
                description="Serverstandort &amp; Datenverarbeitung zwingend in der EU"
                bind:value={is_eu_hosting} />
        </LyGrid3>
    </LyFieldset>

    <LyFieldset mt={true}>
        <LyInputText
            type="area"
            name="todo_message"
            label="Nächste Maßnahmen"
            description="Hausaufgaben für dieses System?"
            placeholder=""
            fullWidth={true}
            autocomplete={true}
            autocorrect={true}
            bind:value={todo_message} />
        <LyGrid2 tag="div" mt={true}>
            <LyInputText
                name="todo_name"
                label="Verantwortlich (Name)"
                description="Wer muss diese Hausaufgaben machen?"
                placeholder=""
                autocomplete={true}
                autocorrect={true}
                bind:value={todo_name} />
            <LyInputDate
                name="todo_till"
                label="Erledigung bis"
                description="Verbindliche Deadline!"
                bind:value={todo_till} />
        </LyGrid2>
    </LyFieldset>

    <LyButtons>
        <LyButton label="Speichern" disabled={disableForm} onclick={trySave} />
    </LyButtons>
</LyForm>

<LyButtons mt={true}>
    <LyButton label="Zurück" color={true} onclick={cancelButton} />
</LyButtons>
